import { endOfMonth, format, startOfMonth } from 'date-fns';
import { FC, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { buttonStyle } from 'components/buttons/buttonStyle';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import Section from 'components/section-menu/Section';
import SectionLayout from 'components/section-menu/SectionLayout';
import { Button } from 'components/ui/button';
import { useLazyGetSalesJournalQuery } from 'store/api/reports';
import { SalesJournalFormValues } from 'types/reports';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { exportSalesJournalXlsx } from './salesJournalExcel';

const SalesJournalForm: FC = () => {
    const defaultValues = useMemo<SalesJournalFormValues>(
        () => ({
            periodStartDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
            periodEndDate: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
        }),
        []
    );

    const methods = useForm<SalesJournalFormValues>({ defaultValues, mode: 'onChange' });
    const { control, handleSubmit } = methods;

    const [fetchSalesJournal, { isFetching }] = useLazyGetSalesJournalQuery();
    const [isExporting, setIsExporting] = useState(false);

    const onSubmit = useCallback(
        async (formValues: SalesJournalFormValues) => {
            const { periodStartDate, periodEndDate } = formValues;
            if (periodStartDate > periodEndDate) {
                errorAlert(t('reports.sales-journal.invalid-period'));
                return;
            }
            setIsExporting(true);
            try {
                const response = await fetchSalesJournal({
                    periodStartDate,
                    periodEndDate,
                    includePrepayments: true,
                }).unwrap();
                await exportSalesJournalXlsx(response, periodStartDate, periodEndDate);
            } catch {
                errorAlert(t('reports.sales-journal.export-error'));
            } finally {
                setIsExporting(false);
            }
        },
        [fetchSalesJournal]
    );

    const actions = useMemo(
        () => [
            <Button
                key="sales-journal-export"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                disabled={isFetching || isExporting}
                onClick={() => {
                    void handleSubmit(onSubmit)();
                }}
            >
                <FormattedMessage id="reports.sales-journal.export" />
            </Button>,
        ],
        [handleSubmit, isFetching, isExporting, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <FormWrapper>
                <ActionHeader title={t('reports.sales-journal.title')} actions={actions} />
                <SectionLayout>
                    <Section title="reports.sales-journal.section-title">
                        <div className="flex max-w-xl flex-col gap-4">
                            <div className="flex flex-wrap gap-4">
                                <div className="w-56">
                                    <DateInput
                                        control={control}
                                        name="periodStartDate"
                                        label="reports.sales-journal.period-start"
                                        fullWidth
                                        required
                                    />
                                </div>
                                <div className="w-56">
                                    <DateInput
                                        control={control}
                                        name="periodEndDate"
                                        label="reports.sales-journal.period-end"
                                        fullWidth
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </Section>
                </SectionLayout>
            </FormWrapper>
        </FormProvider>
    );
};

export default SalesJournalForm;
